import { create } from "zustand";
import type { PlatformRole } from "../lib/rbac";
import { analytics } from "../lib/posthog";
import { isCompleteUser } from "../lib/authUser";

const ACCESS_TOKEN_STORAGE_KEY = "joules_access_token";
const USER_STORAGE_KEY = "joules_user";

export interface AuthUser {
  sub?: string;
  email?: string;
  full_name?: string;
  fullName?: string;
  role?: string;
  // Platform-level role — drives sidebar access control. rbac's union, not
  // data/types' outdated one: these are the values /users/me returns and
  // canAccess() checks.
  platformRole?: PlatformRole;
  [key: string]: unknown;
}

/**
 * An identity that's actually usable: resolved from GET /users/me with a
 * real email and a recognised platform role. This is the only shape ever
 * written to the store (see isCompleteUser).
 */
export type CompleteAuthUser = AuthUser & {
  email: string;
  platformRole: NonNullable<PlatformRole>;
};

interface AuthState {
  accessToken: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  // Bumped by every clearAuth(). An async identity load snapshots it first
  // and only writes its result if it's unchanged — otherwise a load that
  // was in flight during a logout would write a user back afterwards.
  authGeneration: number;
  setTokens: (accessToken: string) => void;
  setUser: (user: CompleteAuthUser) => void;
  clearAuth: () => void;
}

/**
 * Best-effort parse — a corrupt/hand-edited value should never crash boot.
 * Anything short of a complete user (including partial users older builds
 * saved after a failed /users/me) is dropped, so RequireAuth refetches it.
 */
function readStoredUser(): AuthUser | null {
  const raw = sessionStorage.getItem(USER_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (isCompleteUser(parsed)) return parsed;
  } catch {
    // fall through to discard
  }
  sessionStorage.removeItem(USER_STORAGE_KEY);
  return null;
}

// `user` is persisted to `sessionStorage` — the same tab-scoped storage
// accessToken already uses — never to `localStorage`. localStorage is
// shared live by every tab of the origin for as long as they're open, which
// is exactly what let one tab's sign-in silently overwrite another tab's
// identity (a non-admin tab ending up gated against a stale admin `user`
// left behind by a different tab). `sessionStorage` doesn't have that
// property: it's isolated per tab, so it can only ever end up holding this
// tab's own identity. (The one nuance — some browsers clone sessionStorage
// into a brand-new tab opened via "open link in new tab" — only copies an
// internally-consistent snapshot, accessToken and user together, from a
// single instant; it's never an ongoing shared channel the way localStorage
// was, so it doesn't reopen the original hole.) This is what lets a hard
// reload skip re-fetching identity from `/users/me` when a valid one is
// already on hand — RequireAuth still resolves it fresh via
// establishCurrentUser() whenever it's missing (fresh tab, cleared auth).
export const useAuthStore = create<AuthState>()((set) => ({
  accessToken: sessionStorage.getItem(ACCESS_TOKEN_STORAGE_KEY),
  user: readStoredUser(),
  isAuthenticated: !!sessionStorage.getItem(ACCESS_TOKEN_STORAGE_KEY),
  // Counts logouts in this tab. /users/me can still be in flight when the
  // session ends (logout, failed refresh, another tab signing out); without
  // this, that late response would write a user back into a signed-out tab.
  // establishCurrentUser() notes the value before loading and drops the
  // result if clearAuth() has bumped it since. In-memory only: a reload
  // discards any in-flight request along with it.
  authGeneration: 0,

  setTokens: (accessToken) => {
    sessionStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken);
    set({ accessToken, isAuthenticated: true });
  },

  setUser: (user) => {
    sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    set({ user });
  },

  clearAuth: () => {
    sessionStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(USER_STORAGE_KEY);
    analytics.reset();
    set((state) => ({
      accessToken: null,
      user: null,
      isAuthenticated: false,
      authGeneration: state.authGeneration + 1,
    }));
  },
}));
